import axios from 'axios';
import HttpStatus from 'http-status';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { RequestBody, RequestConfig, HttpHeaders, HttpDefaultBody } from 'interface/http';

import config from '../config';

import * as authService from '../services/auth';
import * as tokenService from '../services/token';

import { REFRESH_TOKEN_URL } from '../constants/endpoints';

const instance = axios.create({
  baseURL: config.baseURI,
  responseType: 'json',
  withCredentials: true,
});

let unauthorizedRequestQueue: Array<(refreshedAccessToken: string) => void> = [];
let isRefreshingAccessToken = false;

/**
 * Initialize the unauthorized response interceptors.
 */
instance.interceptors.response.use(
  (response) => response,
  /**
   * This interceptor checks if the response had a 401 status code, which means
   * that the access token used for the request has expired. It then refreshes
   * the access token and resends the original request.
   */
  unauthorizedResponseHandlerInterceptor
);

export async function unauthorizedResponseHandlerInterceptor(err: AxiosError): Promise<AxiosResponse | RequestBody> {
  const originalRequest = err.config;
  if (!originalRequest) {
    throw err;
  }

  const code = err.response?.status;
  const path = originalRequest.url != null ? String(originalRequest.url) : undefined;

  const refreshToken = tokenService.getRefreshToken();
  if (shouldRedirectToLogin(code, path, refreshToken)) {
    throwUnauthorizedError(err);
  }

  if (code === HttpStatus.UNAUTHORIZED && path !== REFRESH_TOKEN_URL) {
    if (!refreshToken) {
      throwUnauthorizedError(err);
    }

    return retryUnauthorizedRequest(originalRequest, refreshToken);
  }

  throw err;
}

function shouldRedirectToLogin(
  code: number | undefined,
  requestUrl: string | undefined,
  refreshToken: string | undefined
): boolean {
  return !refreshToken || (code === HttpStatus.UNAUTHORIZED && requestUrl === REFRESH_TOKEN_URL);
}

function throwUnauthorizedError(err: AxiosRequestConfig | Error): never {
  authService.redirectToLogin();
  throw err instanceof Error ? err : new Error('Unauthorized request');
}

async function retryUnauthorizedRequest(
  originalRequest: AxiosRequestConfig,
  refreshToken: string
): Promise<AxiosResponse | RequestBody> {
  if (isRefreshingAccessToken) {
    return subscribeToAccessTokenRefresh(originalRequest);
  }

  isRefreshingAccessToken = true;

  try {
    const refreshedAccessToken = await refreshAccessToken(refreshToken);
    const newRequest = updateAccessToken(originalRequest, refreshedAccessToken);

    callRequestsFromUnauthorizedQueue(refreshedAccessToken);
    clearUnauthorizedRequestQueue();

    return instance.request(newRequest);
  } catch (error) {
    throwUnauthorizedError(error instanceof Error ? error : new Error('Unable to refresh access token'));
  } finally {
    isRefreshingAccessToken = false;
  }
}

async function refreshAccessToken(refreshToken: string): Promise<string> {
  const refreshedAccessToken = await authService.refresh(refreshToken);

  if (!refreshedAccessToken) {
    throw new Error('Unable to refresh access token');
  }

  tokenService.setAccessToken(refreshedAccessToken);

  return refreshedAccessToken;
}

/**
 * Changes access token of the provided request.
 *
 * @param originalRequest
 * @param newToken
 */
function updateAccessToken(originalRequest: AxiosRequestConfig, newToken: string): AxiosRequestConfig {
  return {
    ...originalRequest,
    headers: {
      ...originalRequest.headers,
      Authorization: `Bearer ${newToken}`,
    },
  };
}

/**
 * Subscribe retry request to access token refresh.
 * Add request to unauthorized request queue.
 *
 * @param originalRequest
 */
function subscribeToAccessTokenRefresh(originalRequest: AxiosRequestConfig): Promise<AxiosResponse> {
  return new Promise((resolve) => {
    unauthorizedRequestQueue.push(function (refreshedAccessToken) {
      const newRequest = updateAccessToken(originalRequest, refreshedAccessToken);

      resolve(instance.request(newRequest));
    });
  });
}

/**
 * Clear unauthorized request queue.
 */
function clearUnauthorizedRequestQueue(): void {
  unauthorizedRequestQueue = [];
}

/**
 * Call pending requests from unauthorized request queue.
 *
 * @param refreshedAccessToken
 */
function callRequestsFromUnauthorizedQueue(refreshedAccessToken: string): void {
  unauthorizedRequestQueue.forEach((cb) => cb(refreshedAccessToken));
}

/**
 * @param url The url for the api request (without the base).
 * @param [config]
 * @param [config.params] An object of queries that will be added to
 * the url.
 * @param [config.accessToken] Whether or not to include the
 * access-token header.
 * @returns */
function get<T = HttpDefaultBody>(url: string, config?: RequestConfig): Promise<T>;
function get<T = HttpDefaultBody>(url: string, config: RequestConfig | undefined, raw: true): Promise<AxiosResponse<T>>;
function get<T = HttpDefaultBody>(
  url: string,
  { params = {}, accessToken = true, responseType = 'json', headers = {} }: RequestConfig = {},
  raw = false
): Promise<T | AxiosResponse<T>> {
  const authHeaders: HttpHeaders = {};

  if (accessToken) {
    authHeaders['Authorization'] = `Bearer ${tokenService.getAccessToken()}`;
  }

  return instance({
    url,
    params,
    responseType,
    method: 'get',
    headers: { ...authHeaders, ...headers },
  }).then((response) => (raw ? response : (response.data as T)) as T | AxiosResponse<T>);
}

/**
 * @param url The url for the api request (without the base).
 * @param [config]
 * @param [config.params] An object of queries that will be added to
 * the url.
 * @param [config.body] An object that will be sent in the request
 * body.
 * @param [config.accessToken] Whether or not to include the
 * access-token header.
 * @returns */
function post<T = HttpDefaultBody>(
  url: string,
  { params = {}, body = {}, accessToken = true, headers = {} }: RequestConfig = {}
): Promise<T> {
  const authHeaders: HttpHeaders = {};

  if (accessToken) {
    authHeaders['Authorization'] = `Bearer ${tokenService.getAccessToken()}`;
  }

  return instance({
    url,
    params,
    data: body,
    method: 'post',
    headers: { ...authHeaders, ...headers },
  }).then((response) => response.data as T);
}

/**
 * @param url The url for the api request (without the base).
 * @param [config]
 * @param [config.params] An object of queries that will be added to
 * the url.
 * @param [config.body] An object that will be sent in the request
 * body.
 * @param [config.accessToken] Whether or not to include the
 * access-token header.
 * @returns */
function put<T = HttpDefaultBody>(
  url: string,
  { params = {}, body = {}, accessToken = true, headers = {} }: RequestConfig = {}
): Promise<T> {
  const authHeaders: HttpHeaders = {};

  if (accessToken) {
    authHeaders['Authorization'] = `Bearer ${tokenService.getAccessToken()}`;
  }

  return instance({
    url,
    params,
    data: body,
    method: 'put',
    headers: { ...authHeaders, ...headers },
  }).then((response) => response.data as T);
}

/**
 * @param url The url for the api request (without the base).
 * @param [config]
 * @param [config.params] An object of queries that will be added to
 * the url.
 * @param [config.body] An object that will be sent in the request
 * body.
 * @param [config.accessToken] Whether or not to include the
 * access-token header.
 * @returns */
function patch<T = HttpDefaultBody>(
  url: string,
  { params = {}, body = {}, accessToken = true, headers = {} }: RequestConfig = {}
): Promise<T> {
  const authHeaders: HttpHeaders = {};

  if (accessToken) {
    authHeaders['Authorization'] = `Bearer ${tokenService.getAccessToken()}`;
  }

  return instance({
    url,
    params,
    data: body,
    method: 'patch',
    headers: { ...authHeaders, ...headers },
  }).then((response) => response.data as T);
}

/**
 * @param url The url for the api request (without the base).
 * @param [config]
 * @param [config.params] An object of queries that will be added to
 * the url.
 * @param [config.body] An object that will be sent in the request
 * @param [config.accessToken] Whether or not to include the
 * access-token header.
 * @returns */
function remove<T = HttpDefaultBody>(
  url: string,
  { params = {}, body = {}, accessToken = true, headers = {} }: RequestConfig = {}
): Promise<T> {
  const authHeaders: HttpHeaders = {};

  if (accessToken) {
    authHeaders['Authorization'] = `Bearer ${tokenService.getAccessToken()}`;
  }

  return instance({
    url,
    params,
    data: body,
    method: 'delete',
    headers: { ...authHeaders, ...headers },
  }).then((response) => response.data as T);
}

export default {
  get,
  put,
  post,
  delete: remove,
  patch,
};
